import { AsyncStatus } from "enums";
import produce from "immer";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { assertUnreachable } from "services/Utils";
import { ReportsFieldType } from "./enums/ReportsFieldType";
import { IField, IRow } from "./ReportsModel";

// We need to escape any double quotes in each field. The regex is the slow point, 
// haven't found any faster way to do this. On large inputs of 100k+ rows, 
// this can lock the browser for a bit, so we're processing the rows in chunks
// to avoid locking up the browser.
const doubleQuotesRx = /"/g;
const chunkSize = 30000;

function generateCSV(fields: IField[], rows: IRow[], onProgress: (progress: number) => void): {
    cancel: () => void,
    resultPromise: Promise<string>,
} {
    // Since we're generating the rows asynchronously, we'll make copies of our input to make sure it doesn't change.
    fields = fields.slice();
    rows = rows.slice();

    // We'll convert each field to a string
    const out: string[][] = [];

    // Create headers
    const headers: string[] = [];
    for (const f of fields) {
        headers.push(`"${f.label}"`);
    }
    out.push(headers);

    // Process rows in chunks
    let isCanceled = false;
    let currentRowIndex = 0;

    return {
        cancel: () => {
            isCanceled = true;
        },
        resultPromise: new Promise<string>((resolve, reject) => {
            // This function processes a chunk of rows, then will set a timeout if more work needs to be done.
            // This avoids locking the browser, so that we can let it render a progress bar.
            const run = () => {
                if (isCanceled) return;

                processChunk(out, fields, rows, currentRowIndex);
                currentRowIndex += chunkSize;

                if (currentRowIndex >= rows.length) {
                    // We've processed all rows, return the result.
                    // Join columns with commas, and rows with CRLF, according to the csv spec.
                    const stringRows: string[] = [];
                    for (const outRow of out) {
                        stringRows.push(outRow.join(`,`));
                    }
                    resolve(stringRows.join(`\r\n`));
                } else {
                    // Not done yet, send progress and wait a tick to process another chunk.
                    onProgress(100 * currentRowIndex/rows.length);
                    setTimeout(() => {
                        run();
                    }, 0);
                }
            }

            // Kick off the job.
            run();
        })
    };
}

// Convert a chunk of the rows to strings, appending the result to "out".
function processChunk(out: string[][], fields: IField[], allRows: IRow[], startIndex: number) {
    const endIndex = Math.min(allRows.length, startIndex + chunkSize);

    for (let i = startIndex; i < endIndex; i++) {
        const row = allRows[i];
        const columns: string[] = new Array(fields.length);

        // Convert each field's value to a quoted string
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            switch (field.type) {
                case ReportsFieldType.String:
                    const escapedVal = (row[field.propName] + '').replace(doubleQuotesRx, `""`);
                    columns[i] = `"${escapedVal}"`;
                    break;
                case ReportsFieldType.Number:
                    columns[i] = `"${row[field.propName]}"`;
                    break;
                case ReportsFieldType.Date:
                    columns[i] = `"${(row[field.propName] as DateTime).toISO()}"`;
                    break;
                default:
                    assertUnreachable(field.type);
            }
        }

        out.push(columns);
    }
}

interface useExportCSVState {
    progress: number, // Goes 0 to 100
    status: AsyncStatus,
}

export function useExportCSV(): {
    progress: number,
    status: AsyncStatus,
    generate: (fields: IField[], rows: IRow[]) => {
        cancel: () => void, 
        resultPromise: Promise<string>
    },
} {
    const [{ status, progress }, setState] = useState<useExportCSVState>({
        progress: 0,
        status: AsyncStatus.Initial,
    })

    const generateCallback = useCallback((fields: IField[], rows: IRow[]) => {
        setState(produce(draft => {
            draft.progress = 0;
            draft.status = AsyncStatus.Loading;
        }));

        // Exporting may take a while, so the model will periodically send a progress event.
        const onProgress = (newProgress: number) => {
            setState(produce(draft => {
                draft.progress = newProgress;
            }));
        }

        const { cancel, resultPromise } = generateCSV(fields, rows, onProgress);

        resultPromise.then(() => {
            setState(produce(draft => {
                draft.progress = 100;
                draft.status = AsyncStatus.Success;
            }));
        }).catch(() => {
            setState(produce(draft => {
                draft.status = AsyncStatus.Error;
            }));
        })

        return {
            cancel: cancel,
            resultPromise: resultPromise,
        };
    }, [])

    return {
        progress: progress,
        status: status,
        generate: generateCallback,
    }
}